import {createFeatureSelector, createSelector} from '@ngrx/store';
import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {BranchState} from './branch.state';
import {Branch, BRANCH_FEATURE_KEY} from '@store/branches/branch.types';
import {generateLoadStatusSelectorsFor, generateSelectorsDependentOn} from '@aviary/store/store.selector-generators';
import {FALLBACK_BRANCH} from './branch.fallback';
import {SelectOptions} from '@aviary/store/common/common.types';
import {convertToSelectOptions} from '@aviary/store/store.helpers';

export const entireState: MxSelector<BranchState> = createFeatureSelector(BRANCH_FEATURE_KEY);

export namespace BranchSelectors {
  export namespace Auth {
    const subState: MxSelector<BranchState['auth']> = createSelector(
      entireState,
      state => state.auth
    );

    export const {
      loading,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const branch: MxSelector<Branch> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_BRANCH
    );

    export const {
      id,
      name,
      phone,
      address,
      address2,
      city,
      state,
      zipCode
    } = generateSelectorsDependentOn(branch, FALLBACK_BRANCH, NO_NAME_PREFIX);
  } // namespace Auth

  export namespace List {
    const subState: MxSelector<BranchState['list']> = createSelector(
      entireState,
      state => state.list
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(subState);

    export const branches: MxSelector<Branch[]> = createSelector(
      subState,
      ({data}) => data
    );

    export const branchIds: MxSelector<number[]> = createSelector(
      branches,
      _branches => _branches.map(b => b.id)
    );

    export const branchesSelectOptions: MxSelector<SelectOptions<number>> = createSelector(
      branches,
      _branches => convertToSelectOptions(_branches, 'name', 'id')
    );

    export const branchNameForId: MxSelectorCreator<[number], string> =
      id => createSelector(
        branches,
        _branches => _branches.find(branch => branch.id === id)?.name ?? ''
      );
  } // namespace List

  export namespace PickupLocations {
    const subState: MxSelector<BranchState['pickupLocations']> = createSelector(
      entireState,
      state => state.pickupLocations
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(subState);

    export const pickupLocations: MxSelector<Branch[]> = createSelector(
      subState,
      ({data}) => data
    );

    export const pickupLocationsSelectOptions: MxSelector<SelectOptions<number>> = createSelector(
      pickupLocations,
      branches => convertToSelectOptions(branches, 'name', 'id')
    );

    export const branchNameForId: MxSelectorCreator<[number], string> =
      id => createSelector(
        pickupLocations,
        _branches => _branches.find(branch => branch.id === id)?.name ?? ''
      );
  } // namespace PickupLocations
}
